/**
 * Sounds: one file (`audio/sounds.json` by default) names the game's sounds,
 * so connections, clips, kits and scripts play a sound by name and changing
 * the file behind it changes it everywhere:
 *
 *   {
 *     "formatVersion": 1,
 *     "preload": ["audio/ui.pack.json"],
 *     "loadLater": ["audio/music.pack.json"],
 *     "sounds": {
 *       "Dial turn": { "keys": ["turn-1", "turn-2", "turn-3"], "volume": -3, "pitch": 0.05 },
 *       "Alchemy theme": { "bus": "music", "keys": ["alchemy-theme"], "loop": true }
 *     },
 *     "buses": {
 *       "music": { "volume": 0.6, "filters": [{ "type": "lowpass", "cutoff": 1200, "enabled": false }] }
 *     }
 *   }
 *
 * A sound's `keys` are audio keys from the asset packs; each play picks one.
 * Every sound plays on a bus (effects, music or ambience), whose volume the
 * player can set and whose filters run in order over everything on it.
 * Scenes start their own music and ambience with their `sound` field.
 * Packs in `loadLater` load in the background after the first scene, so long
 * music never holds a scene up.
 *
 * This module has the file's types and the lookups that need no Phaser. The
 * runtime's `sound(game)` plays them.
 */

/** Where a project keeps its sounds when project.json names none. */
export const SOUNDS_FILE = 'audio/sounds.json';

export const BUSES = ['effects', 'music', 'ambience'] as const;
export type BusName = (typeof BUSES)[number];

export const BUS_LABELS: Record<BusName, string> = { effects: 'Effects', music: 'Music', ambience: 'Ambience' };

export interface SoundsFile {
  formatVersion: number;
  /** Asset packs every scene loads, for sounds any scene may play (button clicks, music that carries on). */
  preload?: string[];
  /** Asset packs that load in the background once the game's first scene has built, for big audio no scene should wait for, such as music. Their sounds play once loaded; music and ambience asked for sooner start when their audio arrives. */
  loadLater?: string[];
  /** The master volume everything starts at, 0 to 1. The player's own setting wins. Default 1. */
  volume?: number;
  sounds?: Record<string, SoundDef>;
  buses?: Partial<Record<BusName, BusDef>>;
}

export interface SoundDef {
  /** Audio keys from the asset packs; each play picks one. */
  keys: string[];
  /** The bus it plays on. Default `effects`. */
  bus?: BusName;
  /** Volume in decibels, from -60 to 12. Default 0. */
  volume?: number;
  /** How much each play's pitch may wander, as a fraction (0.05 is up to 5% either way). Default 0. */
  pitch?: number;
  /** Playback rate: above 1 plays higher and faster, below 1 lower and slower (Godot's pitch_scale). Pitch wobble varies each play around it. Default 1. */
  rate?: number;
  /** How the variation is picked: `random` never picks the same one twice running; `order` goes round them. Default random. */
  pick?: 'random' | 'order';
  /** How many copies may play at once; the oldest stops for a new one. 0 is any. Default 4. */
  max?: number;
  /** Plays until stopped, as music and ambience do. Default: on for the music and ambience buses. */
  loop?: boolean;
}

export interface BusDef {
  /** Starting volume, 0 to 1. The player's own setting, once they move it, wins. Default 1. */
  volume?: number;
  /** Filters over everything on the bus, in order. */
  filters?: BusFilter[];
}

export const BUS_FILTER_TYPES = ['lowpass', 'highpass', 'reverb', 'echo', 'compressor', 'distortion'] as const;
export type BusFilterType = (typeof BUS_FILTER_TYPES)[number];

export interface BusFilterBase {
  type: BusFilterType;
  /** Off filters pass the sound through; games turn them on with `sound(game).filter(...)`. Default true. */
  enabled?: boolean;
}

export interface PassFilter extends BusFilterBase {
  type: 'lowpass' | 'highpass';
  /** Hz. Default 1000. */
  cutoff?: number;
  /** Q, from 0.1. Default 0.7. */
  resonance?: number;
}

export interface ReverbFilter extends BusFilterBase {
  type: 'reverb';
  /** How big the space sounds. Default medium. */
  room?: 'small' | 'medium' | 'large';
  /** How much of the reverb is heard, 0 to 1. Default 0.3. */
  mix?: number;
}

export interface EchoFilter extends BusFilterBase {
  type: 'echo';
  /** Seconds between echoes. Default 0.25. */
  delay?: number;
  /** How much each echo keeps, 0 to 0.95. Default 0.4. */
  feedback?: number;
  /** How much of the echo is heard, 0 to 1. Default 0.3. */
  mix?: number;
}

export interface CompressorFilter extends BusFilterBase {
  type: 'compressor';
  /** dB. Default -24. */
  threshold?: number;
  /** Default 4. */
  ratio?: number;
}

export interface DistortionFilter extends BusFilterBase {
  type: 'distortion';
  /** 0 to 1. Default 0.3. */
  amount?: number;
}

export type BusFilter = PassFilter | ReverbFilter | EchoFilter | CompressorFilter | DistortionFilter;

/** A scene's music and ambience, started when it builds. */
export interface SceneSound {
  /** A sound's name; null stops the music. Left out, whatever is playing carries on, so close-ups and menus over a room keep its music. */
  music?: string | null;
  ambience?: string | null;
  /** How long the change fades over, in ms. Default 800. */
  fade?: number;
}

/** The project's sounds path: project.json's `sounds`, else SOUNDS_FILE. */
export function soundsPathOf(project: { sounds?: string } | undefined): string {
  return project?.sounds || SOUNDS_FILE;
}

/** The bus a sound plays on. */
export function busOf(def: SoundDef | undefined): BusName {
  return def?.bus && (BUSES as readonly string[]).includes(def.bus) ? def.bus : 'effects';
}

/** Whether a sound plays until stopped. */
export function loops(def: SoundDef | undefined): boolean {
  return def?.loop ?? (busOf(def) === 'music' || busOf(def) === 'ambience');
}

/** Decibels as a gain. */
export function dbToGain(db: number | undefined): number {
  return db ? Math.pow(10, db / 20) : 1;
}

/** The names of the sounds a scene, clip or connection list names. */
export function sceneSoundNames(sound: SceneSound | undefined): string[] {
  return [sound?.music, sound?.ambience].filter((s): s is string => typeof s === 'string' && s !== '');
}

/** A filter's sentence for the mixer, such as "Low-pass 1200 Hz". */
export function describeBusFilter(f: BusFilter): string {
  switch (f.type) {
    case 'lowpass':
      return `Low-pass ${f.cutoff ?? 1000} Hz`;
    case 'highpass':
      return `High-pass ${f.cutoff ?? 1000} Hz`;
    case 'reverb':
      return `Reverb ${f.room ?? 'medium'} ${Math.round((f.mix ?? 0.3) * 100)}%`;
    case 'echo':
      return `Echo ${f.delay ?? 0.25} s`;
    case 'compressor':
      return `Compressor ${f.threshold ?? -24} dB`;
    case 'distortion':
      return `Distortion ${Math.round((f.amount ?? 0.3) * 100)}%`;
  }
}

/**
 * The game's sounds file with content packs' sounds files on top, in mount
 * order: each pack's sounds are added, replacing the game's (or an earlier
 * pack's) of the same name, and its `preload` and `loadLater` asset packs are
 * added to the game's. Buses and the master volume stay the game's: the mixer
 * is the game's, not a pack's.
 */
export function mergeSounds(base: SoundsFile | undefined, extra: readonly (SoundsFile | undefined)[]): SoundsFile | undefined {
  const more = extra.filter((f): f is SoundsFile => !!f);
  if (!more.length) return base;
  const list = (k: 'preload' | 'loadLater') => {
    const all = [...new Set([base, ...more].flatMap((f) => f?.[k] ?? []))];
    return all.length ? { [k]: all } : {};
  };
  return {
    ...(base ?? { formatVersion: 1 }),
    ...list('preload'),
    ...list('loadLater'),
    sounds: Object.assign({}, base?.sounds, ...more.map((f) => f.sounds)),
  };
}
